import React from "react";
import styled from "styled-components";

import { ContactType } from "../../../../shared/core/contact/contact";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { contactService } from "../../../../shared/core/service/services";
import { isDefined } from "../../../../shared/utils/assert";
import { buildLinkMailTo, buildLinkPhoneTo } from "../../../../shared/utils/link-builder";
import { CONTACT_SUPPORT_MODAL_ID } from "../../../core/modal/modal-id";
import { MarkdownText } from "../../common/forms/markdown-text";
import { WhiteModalContainer } from "../../common/modal/white-modal-container";
import { ContactEmailIcon } from "../../common/svg/contact-email-icon";
import { ContactPhoneIcon } from "../../common/svg/contact-phone-icon";
import { theme } from "../../styles/theme";

interface ContactSupportModalProps {
  title: string;
  subtitle: string;
}

export const ContactSupportModal = (props: ContactSupportModalProps) => {
  const { title, subtitle } = props;
  const { formatMessage } = useIntl();
  const contacts = contactService.getContacts();
  const phone = contacts.get(ContactType.Phone);
  const email = contacts.get(ContactType.Email);

  return (
    <WhiteModalContainer closeButton id={CONTACT_SUPPORT_MODAL_ID}>
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>
      {/* A row is dropped when the tenant carries no such contact, rather than printing a blank. */}
      {isDefined(phone) || isDefined(email) ? (
        <Contacts>
          {isDefined(phone) && (
            <ContactRow href={buildLinkPhoneTo(phone)}>
              <RowGlyph as={ContactPhoneIcon} aria-hidden="true" />
              <RowLabel>{formatMessage("contactSupport.contactByPhone", { phoneNumber: phone })}</RowLabel>
            </ContactRow>
          )}
          {isDefined(email) && (
            <ContactRow href={buildLinkMailTo(email)}>
              <RowGlyph as={ContactEmailIcon} aria-hidden="true" />
              <RowLabel>{formatMessage("contactSupport.contactByEmail", { email })}</RowLabel>
            </ContactRow>
          )}
        </Contacts>
      ) : (
        <NoContact>{formatMessage("contactSupport.noContactAvailable")}</NoContact>
      )}
    </WhiteModalContainer>
  );
};

const Title = styled(MarkdownText)`
  ${theme.boldText};
  margin: 0;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const Subtitle = styled.div`
  ${theme.text};
  margin: 8px 0 20px;
  font-size: 15px;
  line-height: 20px;
  color: ${theme.colors.gray[800]};
`;

const Contacts = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
`;

const ContactRow = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-top: 1px solid ${theme.colors.gray[100]};
  color: inherit;

  &:first-of-type {
    border-top: 0;
  }

  &:hover {
    background-color: ${theme.colors.gray[50]};
  }
`;

const RowGlyph = styled.svg`
  width: 26px;
  height: 26px;
  flex-shrink: 0;

  path {
    fill: ${theme.colors.blue[700]};
  }
`;

const RowLabel = styled.span`
  ${theme.text};
  font-size: 15px;
  line-height: 20px;
  color: ${theme.colors.blue[800]};
`;

const NoContact = styled.div`
  ${theme.text};
  padding: 20px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  font-size: 14px;
  line-height: 19px;
  color: ${theme.colors.gray[800]};
`;
